import { createEntityAdapter, createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { showMessage } from 'app/store/fuse/messageSlice';
import axios from 'axios';
import { buildSalesDashboardRequest } from 'app/main/apps/staff/dashboard/buildSalesDashboardRequest';

export const getWidgets = createAsyncThunk('DashboardApp/widgets/getWidgets', async () => {
  const response = await axios.get('/api/widgetData');
  const data = await response.data.widgets;
  return data;
});

export const getChartData = createAsyncThunk('DashboardApp/widgets/getTopSales', async (params) => {
  const response = await axios.get('/api/graphApi', { params })
  const data = await response.data.orders
  return data;
})

export const getSalesDashboardData = createAsyncThunk(
  'DashboardApp/widgets/getSalesDashboardData',
  async (params) => {
    const payload = buildSalesDashboardRequest(params);
    const response = await axios.post('/api/metrics', payload);
    return response.data;
  }
);

export const getEcomWidgetsOrder = createAsyncThunk(
  'DashboardApp/widgets/getEcomWidgetsOrder',
  async (inputData) => {
    if (inputData.stage === 'completed') inputData.stage = 'submitted'
    const response = await axios.post('/api/widgetOrderData', inputData);
    const data = await response.data.orders;
    return data;
  }
);

// A new thunk, not a variant of getEcomWidgetsOrder above: status "Actionable" (PGSS-5632) is
// server-filtered to the statuses this user's roles act on, sorted and capped server-side, and the
// response also carries a real `total` and a `summary` of counts (by status, by how long items have
// waited) over every actionable item -- getEcomWidgetsOrder's callers only ever unwrap `.orders`, so
// reusing it here would silently drop both. `sort` is 'oldest' (longest waiting first) or 'amount'.
export const getDashboardActionQueue = createAsyncThunk(
  'DashboardApp/widgets/getDashboardActionQueue',
  // `statuses` (PGSS-5653) narrows the queue to some statuses -- one queue of a role that has several; the
  // backend keeps only statuses the caller acts on or may view, never beyond those.
  // `overdue` (PGSS-5632) keeps only items waiting 7+ days, applied by the backend before its row limit.
  // `mine` (PGSS-5640, a sales rep) keeps only the orders whose sales person is the caller; the backend resolves
  // who that is, so no id is sent.
  async ({ stage, limit, sort, statuses, overdue, mine }) => {
    const response = await axios.post('/api/widgetOrderData', {
      stage,
      status: 'Actionable',
      limit,
      sort,
      ...(statuses && { statuses }),
      ...(overdue && { overdue: true }),
      ...(mine && { mine: true }),
    });
    return {
      orders: response.data.orders || [],
      total: response.data.total ?? 0,
      summary: response.data.summary || null,
    };
  }
);

// PGSS-5653: the redesigned Home's counters for the caller's role (one each for the VAS manager and the VAS
// production user today): a list of { key, count }, empty for most roles.
export const getHomeCounters = createAsyncThunk('DashboardApp/widgets/getHomeCounters', async () => {
  const response = await axios.get('/api/homeCounters');
  return Array.isArray(response.data) ? response.data : [];
});

// The total the RFQ/Quote/Order list pages show for a stage (every status, not just actionable):
// same GET /api/orders the list uses, one row per page, count from the pagination header. With a
// `salesPersonId` (PGSS-5640) it is that sales person's total, as the list's own Sales person filter counts it.
export const getStageListTotal = createAsyncThunk('DashboardApp/widgets/getStageListTotal', async ({ stage, salesPersonId = null }) => {
  const response = await axios.get('/api/orders', {
    params: { stage, ...(salesPersonId && { sales_person: salesPersonId }) },
    headers: { 'App-Pagination-Limit': 1, 'App-Pagination-Num': 0 },
  });
  return Number(response.headers?.['app-pagination-total-records']);
});

export const getEcomWidgetsOrderItems = createAsyncThunk(
  'DashboardApp/widgets/getEcomWidgetsOrderItems',
  async (params) => {
    const response = await axios.post(`/api/widgetOrderItemData`, null, { params });
    const getResponse = () => {
      switch (params.stage) {
        case 'invoice':
          if (params?.invoiceId) {
            return response.data
          }
          return response.data.invoice;
        case 'shipment':
          if (params?.is_outbound) {
            return response.data.shipping_label
          }
          return response.data.shipping_label.orders;
        case 'in-production':
          return response.data.shipping_label.orders;
        case 'container-management':
          return response.data.shipping_label.orders;
        default:
          return response.data.orderItems
      }
    }
    const data = await getResponse();
    return data;
  }
);

export const getShipmentInfo = createAsyncThunk(
  'DashboardApp/widgets/getShipmentInfo',
  async (params) => {
    const response = await axios.get(`/api/getShipmentInfo`, { params });
    const data = response.data.orders
    return data;
  }
);

export const viewNote = createAsyncThunk('/api/viewNote', async ({ id }) => {
  const response = await axios.get(`/api/viewNote/${id}`)
  return response.data.note_history
})

export const viewNotes = createAsyncThunk('/api/viewNotes', async (params) => {
  const response = await axios.get(`/api/viewNotes`, { params })
  const data = response.data?.notes;
  return data
})

export const getOrderNotes = createAsyncThunk('/api/getOrderNotes', async ({ id }) => {
  const response = await axios.get(`/api/getOrderNotes/${id}`)
  return response.data.notes
})

export const getContainerNumbers = createAsyncThunk('/api/getContainerNumbers', async () => {
  const response = await axios.get('/api/getContainerNumbers');
  return response.data;
})

export const getOrdersOfContainerNumber = createAsyncThunk('/api/getContainerNumbers', async (params) => {
  const response = axios.get('/api/receiveOrders', { params });
  return response;
})

export const recieveOrder = createAsyncThunk('/api/recieveOrder', async (params) => {
  const response = axios.put('/api/receiveOrders', params)
  return response
})

export const getEcomWidgetsInvoice = createAsyncThunk(
  'DashboardApp/widgets/getEcomWidgetsInvoice',
  async (inputData) => {
    const response = await axios.post('/api/widgetInvoiceData', inputData);
    const data = await response.data.invoice;
    return data;
  }
);

export const getEcomWidgetsRfq = createAsyncThunk(
  'DashboardApp/widgets/getEcomWidgetsRfq',
  async (orderId) => {
    const response = await axios.post('/api/widgetRfqData', orderId);
    const data = await response.data;
    return data;
  }
);

export const makeItReview = createAsyncThunk(
  'DashboardApp/widgets/makeItReview',
  async (payload, { dispatch, getState }) => {
    const state = getState()
    const response = await axios.put(`/api/orders/${payload.id}`,
      { ...payload, status: state.mainDashboardApp.widgets.statuses.find((s) => s.name === 'In-Review') });
    const data = await response.data.order;
    dispatch(
      showMessage({
        message: 'Status updated successfully',
        variant: 'info',
        anchorOrigin: {
          vertical: 'top',
          horizontal: 'right',
        },
      })
    );
    return data;
  }
);

export const getStatuses = createAsyncThunk(
  'DashboardApp/widgets/getStatuses',
  async (params) => {
    const response = await axios.get('/api/categoryValues', { params: { c: params } });
    const resData = await response.data;
    const data =  resData?.filter(r => r.name !== 'Inventory Received')
    return { data, params };
  }
);

export const confirmInvoice = createAsyncThunk('DashboardApp/widgets/confirmInvoice',
  async ({ id, param }) => {
    const response = await axios.put(`/api/invoice/${id}`, param);
    return response;
  }
)

export const updateInvoice = createAsyncThunk('DashboardApp/widgets/updateInvoice',
  async ({ id, param }) => {
    const response = await axios.put(`/api/updateInvoice/${id}`, param);
    return response;
  }
)

export const confirmSupplierInvoice = createAsyncThunk('DashboardApp/widgets/confirmInvoice',
  async ({ id }) => {
    const response = await axios.put(`/api/shipment/${id}`, {
      confirm_invoice: true
    });
    return response;
  }
)

const widgetsAdapter = createEntityAdapter({});

export const { selectEntities: selectWidgets } = widgetsAdapter.getSelectors(
  (state) => state.mainDashboardApp.widgets
);

const initialValue = {
  props: {
    open: false,
  },
  data: null,
  param: null,
  dashboard: true,
  statuses: [],
  shipmentInfo: [],
};

const defaultField = 'viewOrderDialog';

export const initState = {
  loading: false,
  viewOrderDialog: initialValue,
  orderDialog: initialValue,
  confirmInvoiceDialog: initialValue,
  default: 'viewOrderDialog',
  recieveOrderDialog: { props: false },
  ordersOfContainerNumber: null
};

const widgetsSlice = createSlice({
  name: 'DashboardApp/widgets',
  initialState: initState,
  reducers: {
    openRecieveOrderDialog: (state, action) => {
      state.recieveOrderDialog = {
        props: {
          open: true,
        },

      }
    },
    closeRecieveOrderDialog: (state, action) => {
      state.recieveOrderDialog = {
        props: {
          open: false
        }
      }
    },
    openViewOrderDialog: (state, action) => {
      state[action?.payload?.field || defaultField] = {
        props: {
          open: true,
        },
        stage: action.payload.stage,
        data: action.payload.data,
        param: action.payload.param,
        completeOrderData: action.payload.completeOrderData,
        dashboard: true,
      };
    },
    closeViewOrderDialog: (state, action) => {
      state[action?.payload?.field || defaultField] = {
        props: {
          open: false,
        },
        data: null,
        param: null,
        dashboard: true,
      };
    },
    setOrdersOfContainerNumber: (state, action) => {
      state.ordersOfContainerNumber = action.payload
    },
  },
  extraReducers: {
    [getWidgets.fulfilled]: widgetsAdapter.setAll,
    [getStatuses.fulfilled]: (state, action) => {
      const { data, params } = action.payload;
      if (!params || (params && params !== 'customer_internal_status')) {
        state.statuses = data;
      }
    },
    [getStatuses.rejected]: (state, action) => {
      const { params } = action.payload;
      if (!params || (params && params !== 'customer_internal_status')) {
        state.statuses = []
      }
    },
    [getShipmentInfo.fulfilled]: (state, action) => {
      state.shipmentInfo = action.payload
    }
  },
});



export const { openViewOrderDialog, closeViewOrderDialog, openRecieveOrderDialog, closeRecieveOrderDialog, setOrdersOfContainerNumber } = widgetsSlice.actions;

export default widgetsSlice.reducer;
